/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICommerceApiSettings, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { versionLt } from '@msdyn365-commerce/core-internal';
import { ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync, getProductRatingsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import getSimpleProducts, { ProductInput } from './get-simple-products';
import { buildCacheKey, generateKey, getProductDetailsCriteriaFromActionInput } from './utilities/utils';

/**
 * Full Product Input.
 */
export class FullProductInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public ProductDetailsCriteria: ProductDetailsCriteria;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(productId: number | string, apiSettings: ICommerceApiSettings, criteria: ProductDetailsCriteria) {
        this.productId = typeof productId === 'string' ? Number.parseInt(productId, 10) : productId;
        this.ProductDetailsCriteria = criteria;
        this.apiSettings = apiSettings;
        this.channelId = apiSettings.channelId;
    }

    public getCacheKey = () => {
        const { getPrice, getRating } = this.ProductDetailsCriteria;
        return buildCacheKey(generateKey([this.productId, getPrice, getRating]), this.apiSettings);
    };

    public getCacheObjectType = () => 'FullProduct';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * Class to define criteria to get full product like price, ratings etc.
 */
export class ProductDetailsCriteria {
    public getPrice: boolean;

    public getRating: boolean;

    constructor(getPrice?: boolean, getRating?: boolean) {
        this.getPrice = getPrice || false;
        this.getRating = getRating || false;
    }
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput[] => {
    let productIds = inputData.config && inputData.config.productIds;
    const productDetailsCriteria = getProductDetailsCriteriaFromActionInput(inputData);
    if (typeof productIds === 'string') {
        productIds = productIds.split(',');
    }
    if (Array.isArray(productIds) && productIds.length > 0) {
        return productIds.map((productId: string) => {
            return new FullProductInput(+productId, inputData.requestContext.apiSettings, productDetailsCriteria);
        });
    }
    return [];
};

/**
 * Calls the Retail API and returns the product based on the passed ProductInput.
 * @param inputs
 * @param ctx
 */
export async function getFullProductsAction(inputs: FullProductInput[], ctx: IActionContext): Promise<FullProduct[]> {
    if (!Array.isArray(inputs) || inputs.length === 0) {
        ctx.trace('[getFullProductsAction] Invalid or empty inputs passed.');
        return <FullProduct[]>{};
    }

    const { apiSettings } = ctx.requestContext;
    const productInputs = inputs.map(
        (input: FullProductInput): ProductInput => {
            return new ProductInput(input.productId, apiSettings);
        }
    );

    return getSimpleProducts(productInputs, ctx)
        .then(result => {
            return result.map(product => {
                return { ProductDetails: product };
            });
        })
        .then((productCollection: FullProduct[]) => {
            const validProductIds = new Set(productCollection.map(input => input.ProductDetails && input.ProductDetails.RecordId));

            const ratingsAndPricePromises = [
                _getActivePrices(inputs, validProductIds, productCollection, ctx),
                _getProductRatings(inputs, validProductIds, productCollection, ctx)
            ];
            return Promise.all(ratingsAndPricePromises).then(() => {
                return productCollection;
            });
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('Unable to get Simple products');
            return [];
        });
}

async function _getActivePrices(
    inputs: FullProductInput[],
    validProductIds: Set<number>,
    productCollection: FullProduct[],
    ctx: IActionContext
): Promise<void> {
    const projectDomain: ProjectionDomain = { ChannelId: +ctx.requestContext.apiSettings.channelId, CatalogId: 0 };
    const validInputs = <number[]>inputs
        .map(input => {
            if (input.ProductDetailsCriteria.getPrice && validProductIds.has(input.productId)) {
                return input.productId;
            }
        })
        .filter(Boolean);

    return getActivePricesAsync(
        { callerContext: ctx, queryResultSettings: {} },
        projectDomain,
        validInputs,
        new Date(),
        null,
        [],
        true
    ).then(result => {
        result.forEach(productPrice => {
            const fullProduct: FullProduct | undefined = productCollection.find(x => {
                return x.ProductDetails && x.ProductDetails.RecordId === productPrice.ProductId;
            });
            if (fullProduct) {
                // If RS Verison < 9.16.0 (aka 10.0.6), customer contextual price won't be
                // included so instead just use AdjustedPrice
                if (versionLt(ctx.requestContext.apiSettings.retailServerProxyVersion, '9.16.0')) {
                    productPrice.CustomerContextualPrice = productPrice.AdjustedPrice;
                }
                fullProduct.ProductPrice = productPrice;
            }
        });
    })
        .catch((error: Error) => {
            const telemetry = ctx.telemetry;
            telemetry.debug('[getActivePricesAsync] Unable to get active price.');
            telemetry.exception(error);
        });
}

async function _getProductRatings(
    inputs: FullProductInput[],
    validProductIds: Set<number>,
    productCollection: FullProduct[],
    ctx: IActionContext
): Promise<void> {
    const validInputs = <number[]>inputs
        .map(input => {
            if (input.ProductDetailsCriteria.getRating && validProductIds.has(input.productId)) {
                return input.productId;
            }
        })
        .filter(Boolean);

    return getProductRatingsAsync({ callerContext: ctx, queryResultSettings: {} }, validInputs)
        .then(result => {
            result.forEach(productRating => {
                const fullProduct: FullProduct | undefined = productCollection.find(x => {
                    return x.ProductDetails && x.ProductDetails.RecordId === productRating.ProductId;
                });
                if (fullProduct) {
                    fullProduct.ProductRating = productRating;
                }
            });
        })
        .catch(error => {
            const telemetry = ctx.telemetry;
            telemetry.debug('[getProductRatingsAsync] Unable to get product ratings.');
            telemetry.exception(error);
        });
}

export const getFullProductsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-full-products',
    action: <IAction<FullProduct[]>>getFullProductsAction,
    input: createInput,
    isBatched: true
});

export default getFullProductsActionDataAction;
